"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WarehouseClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_objects_1 = require("@deliverr/commons-objects");
const InventoryByLocationResponse_1 = require("./InventoryByLocationResponse");
const ReceiptAsnResponse_1 = require("./ReceiptAsnResponse");
const ReceiveAsnResponse_1 = require("./ReceiveAsnResponse");
const ReceiveLocationResponse_1 = require("./ReceiveLocationResponse");
const WarehouseApiClient_1 = require("./WarehouseApiClient");
const commons_utils_1 = require("@deliverr/commons-utils");
const client_auth_1 = require("@deliverr/client-auth");
const capMapToObj_1 = require("./capMapToObj");
const commons_logs_1 = require("@deliverr/commons-logs");
const MILLISECONDS_PER_MINUTE = 60000;
class WarehouseClient extends WarehouseApiClient_1.WarehouseApiClient {
    constructor(config = {}) {
        const WAREHOUSE_SERVICE_URL = (0, commons_utils_1.getEnvString)("WAREHOUSE_SERVICE_URL", "");
        const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
        super({
            ...config,
            baseURL: `${config.baseURL || WAREHOUSE_SERVICE_URL}`,
            authEngine: commons_objects_1.AuthEngine.COGNITO_INTERNAL,
            tokenRefreshFn: tokenHandler.getTokenFn(),
        });
        if (!WAREHOUSE_SERVICE_URL) {
            commons_logs_1.log.warn("WAREHOUSE_SERVICE_URL environment variable is not set");
        }
    }
    /**
     * Returns warehouse's 3PL dashboard link as string -> iframeUrl
     * @param warehouseId - string ID of the warehouse like "WAR_ROC"
     * @throws WAREHOUSE {warehouseId} NOT FOUND -  when the warehouse ID is not found.
     */
    async getWarehouseDashboard(warehouseId, warehouseDashboard) {
        try {
            return await this.getCached(`/DATA/v1/warehouse/dashboard/${warehouseId}?warehouseDashboard=${warehouseDashboard}`);
        }
        catch (ex) {
            return this.handleError("getWarehouseDashboard", ex, warehouseId, warehouseDashboard);
        }
    }
    /**
     * Returns warehouse's receiving dashboard link as string -> iframeUrl
     * @param warehouseId - string ID of the warehouse like "WAR_ROC"
     * @throws WAREHOUSE {warehouseId} NOT FOUND -  when the warehouse ID is not found.
     */
    async getWarehouseReceivingDashboard(warehouseId) {
        try {
            return await this.getCached(`/DATA/v2/warehouse/dashboard/receiving/${warehouseId}`);
        }
        catch (ex) {
            return this.handleError("getWarehouseReceivingDashboard", ex, warehouseId);
        }
    }
    /**
     *
     * @param warehouseId
     * @returns Warehouse: the core warehouse data, and not the sub-data such as boxes, and shipmethods
     */
    async getBasicWarehouseData(warehouseId) {
        try {
            return await this.getCached(`/DATA/v2/warehouses/${warehouseId}`, undefined, 10 * MILLISECONDS_PER_MINUTE + this.getJitterValue(2 * MILLISECONDS_PER_MINUTE));
        }
        catch (ex) {
            return this.handleError("getBasicWarehouseData", ex, warehouseId);
        }
    }
    /**
     * Returns warehouse along with all the meta-data we have.
     * @param id - string ID of the warehouse like "DM_CA_FAT1"
     * @throws NO_WAREHOUSE_FOUND -  when the warehouse ID is not found.
     */
    async getWarehouse(id, verbose = false) {
        if (verbose) {
            commons_logs_1.log.info(`getWarehouseData for warehouse ${id}`);
        }
        try {
            return await this.getCached(`/DATA/v1/warehouses/${id}`, undefined, 10 * MILLISECONDS_PER_MINUTE + this.getJitterValue(2 * MILLISECONDS_PER_MINUTE));
        }
        catch (ex) {
            return this.handleError("getWarehouse", ex, id);
        }
    }
    /**
     * Returns a batch of warehouses along with all the meta-data we have.
     * @param [id] - A list of string IDs of the warehouses like ["DM_CA_FAT1", "OWD_MIR"]
     * @throws NO_WAREHOUSE_FOUND -  when the warehouse ID is not found.
     */
    async getWarehouses(ids, verbose = false) {
        if (verbose) {
            commons_logs_1.log.info({ ids }, "getWarehouseData for warehouses");
        }
        try {
            const results = await Promise.allSettled(ids.map(async (id) => this.getCached(`/DATA/v1/warehouses/${id}`, undefined, 10 * MILLISECONDS_PER_MINUTE + this.getJitterValue(2 * MILLISECONDS_PER_MINUTE))));
            const successfulResults = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
            const rejectedResults = results.filter((result) => result.status === "rejected").map((result) => result.reason);
            if (rejectedResults.length > 0) {
                commons_logs_1.log.info({ rejectedResults }, "Getting errors for particular warehouses");
            }
            return successfulResults;
        }
        catch (ex) {
            return this.handleError("getWarehouse", ex, ids.toString());
        }
    }
    /**
     * Returns a batch of simple warehouse information,
     * (without additional metadata - no shipping methods, package types, carrier accounts).
     * @param [ids] - A list of string IDs of the warehouses like ["PBD_ALP", "OWD_MIR"]
     * @throws NO_WAREHOUSE_FOUND -  when the warehouse ID is not found.
     */
    async getWarehousesByIds(ids) {
        try {
            const results = await Promise.allSettled(ids.map(async (id) => (await this.getCached(`/DATA/v2/warehouses/${id}`, undefined, 10 * MILLISECONDS_PER_MINUTE)).warehouse));
            const successfulResults = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
            const rejectedResults = results.filter((result) => result.status === "rejected").map((result) => result.reason);
            if (rejectedResults.length > 0) {
                commons_logs_1.log.info({ rejectedResults }, "Getting errors for particular warehouses");
            }
            return successfulResults;
        }
        catch (ex) {
            return this.handleError("getWarehousesByIds", ex, ids.toString());
        }
    }
    async getWarehouseCarrierAccounts(id) {
        try {
            return (await this.getCached(`/DATA/v2/warehouses/carrieraccount/${id}`, undefined, 10 * MILLISECONDS_PER_MINUTE)).carrierAccounts;
        }
        catch (ex) {
            return this.handleError("getWarehouseCarrierAccounts", ex, id.toString());
        }
    }
    /**
     * Returns a list of shipping methods at the given warehouse
     * @param [id] - A warehouse ID: "PBD_ALP"
     */
    async getWarehouseShippingMethods(id) {
        try {
            return (await this.getCached(`/DATA/v2/warehouses/shippingmethod/${id}`, undefined, 10 * MILLISECONDS_PER_MINUTE)).shippingMethods;
        }
        catch (ex) {
            return this.handleError("getWarehouseShippingMethods", ex, id.toString());
        }
    }
    /**
     * Returns warehouse ids that are enabled for fulfillment
     */
    async getAvailableWarehouseIds() {
        commons_logs_1.log.info(`getAvailableWarehouseIds`);
        try {
            return await this.getCached("/DATA/v1/warehouses/ids");
        }
        catch (ex) {
            return this.handleError("getAvailableWarehouseIds", ex);
        }
    }
    /**
     * Returns warehouse provider by provider code
     */
    async getWarehouseProvider(providerCode) {
        try {
            return await this.getCached(`/DATA/v1/warehouse-provider/${providerCode}`);
        }
        catch (ex) {
            return this.handleError("getWarehouseProvider", ex);
        }
    }
    /**
     * Returns warehouse ids with a specific capability
     */
    async getWarehousesByCapability(capability, verbose = false) {
        if (verbose) {
            commons_logs_1.log.info({ fn: `getWarehousesByCapability`, capability });
        }
        try {
            return await this.getCached(`/DATA/v1/warehouses/ids`, {
                [capability.toString()]: true,
            });
        }
        catch (ex) {
            return this.handleError("getWarehousesByCapability", ex);
        }
    }
    /**
     * Returns provider ids with warehouses that have specific capability
     */
    async getProvidersByCapability(capability, value) {
        try {
            return await this.getCached(`/DATA/v1/warehouses/providers`, {
                [capability.toString()]: value,
            });
        }
        catch (ex) {
            return this.handleError("getProvidersByCapability", ex);
        }
    }
    /**
     * Returns warehouse ids with specific capabilities enabled
     */
    async getWarehousesByCapabilities(capabilities, verbose = false) {
        if (verbose) {
            commons_logs_1.log.info({ fn: `getWarehousesByCapabilities`, capabilities });
        }
        try {
            return await this.getCached(`/DATA/v1/warehouses/ids`, capabilities.reduce((mapped, capability) => ({
                ...mapped,
                [capability]: true,
            }), {}));
        }
        catch (ex) {
            return this.handleError("getWarehousesByCapabilities", ex);
        }
    }
    /**
     * Returns warehouse ids with specific capabilities enabled or disabled
     */
    async getWarehousesByCapabilitiesMap(capabilityMap, verbose = false) {
        if (verbose) {
            commons_logs_1.log.info({ fn: `getWarehousesByCapabilitiesMap`, capabilityMap });
        }
        try {
            return await this.getCached(`/DATA/v1/warehouses/ids`, (0, capMapToObj_1.capMapToObj)(capabilityMap));
        }
        catch (ex) {
            return this.handleError("getWarehousesByCapabilitiesMap", ex);
        }
    }
    /**
     * Returns warehouse ids with specific capabilities enabled or disabled
     */
    async getWarehousesByCapabilitiesMapAndNodeTypes(capabilityMap, nodeTypes, verbose = false) {
        if (verbose) {
            commons_logs_1.log.info({ fn: `getWarehousesByCapabilitiesMap`, capabilityMap });
        }
        const nodeTypesString = nodeTypes ? `[${nodeTypes.join(",")}]` : undefined;
        const capMapAndNodeType = { ...(0, capMapToObj_1.capMapToObj)(capabilityMap), nodeTypes: nodeTypesString };
        try {
            return await this.getCached(`/DATA/v1/warehouses/ids`, capMapAndNodeType);
        }
        catch (ex) {
            return this.handleError("getWarehousesByCapabilitiesMap", ex);
        }
    }
    async getAllWarehouseProviders() {
        try {
            return await this.getCached(`/DATA/v1/warehouses/providers`);
        }
        catch (ex) {
            return this.handleError("getAllWarehouseProviders", ex);
        }
    }
    async updateWarehouse(warehouse) {
        try {
            return await this.post(`/DATA/v2/warehouses/warehouse`, {
                warehouse,
            });
        }
        catch (ex) {
            return this.handleError("updateWarehouse", ex, warehouse.id, warehouse);
        }
    }
    /**
     * Returns the warehouse specifc boxes for a warehouse
     * @param warehouseId
     * @throws NO_WAREHOUSE_FOUND - when the warehouse is not found
     * @throws NO_BOXES_FOUND - when there is no box data for that warehouse
     */
    async getWarehouseBoxes(warehouseId) {
        try {
            return await this.getCached(`/DATA/v1/warehouses/boxes/${warehouseId}`);
        }
        catch (ex) {
            return this.handleError("getWarehouseBoxes", ex, warehouseId);
        }
    }
    /**
     * Returns the flat rate boxes available for a warehouse
     * @param warehouseId
     * @throws NO_WAREHOUSE_FOUND - when the warehouse is not found
     * @throws NO_BOXES_FOUND - when there is no box data for that warehouse
     */
    async getFlatRateBoxes(warehouseId) {
        try {
            return await this.getCached(`/DATA/v1/warehouses/flat-rate-boxes/${warehouseId}`);
        }
        catch (ex) {
            return this.handleError("getFlatRateBoxes", ex, warehouseId);
        }
    }
    /**
     * Send the order to the warehouse.
     * @param warehouseOrder - order to be created on the warehouse side
     * @param warehouseId - the ID of the warehouse that you want to send the order to
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseSendToWarehouseErrorCode
     */
    async sendToWarehouse(warehouseId, warehouseOrder) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/shipments`, warehouseOrder);
        }
        catch (ex) {
            return this.handleError("sendToWarehouse", ex, warehouseId, warehouseOrder);
        }
    }
    /**
     * Send a document to the warehouse.
     * @param documentRequest -  info bundle
     * @throws WarehouseCommonErrorCode
     */
    async submitDocument(documentRequest) {
        const warehouseId = documentRequest.warehouseId;
        const shipmentId = documentRequest.shipmentId;
        const fileName = documentRequest.fileName;
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/shipments/${shipmentId}/upload-document/${warehouseId}`, documentRequest);
        }
        catch (ex) {
            this.handleError("submitDocument", ex, warehouseId, fileName);
        }
    }
    /**
     * Gets the shipment status from the warehouse
     * @param warehouseId - ID to query status from
     * @param shipmentId - the shipment ID you are looking up
     * @returns undefined - when the shipment is not found
     * @returns {WarehouseShipment} when it is
     * @throws UNKNOWN_ERR
     */
    async getShipment(warehouseId, shipmentId) {
        try {
            // make sure that shipmentId is a string, otherwies we'll get a 500...
            const response = await this.getShipments(warehouseId, [String(shipmentId)]);
            return response.shipments[0];
        }
        catch (ex) {
            return this.handleError("getShipment", ex, warehouseId, shipmentId);
        }
    }
    /**
     * @returns WarehouseProduct - when product creation is successful
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseCreateProductErrorCode
     */
    async createProduct(warehouseId, product) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/products`, {
                product,
            });
        }
        catch (ex) {
            return this.handleError("createProduct", ex, warehouseId, product);
        }
    }
    /**
     * @returns WarehouseProduct - when product update is successful
     * @throws WarehouseErrorCode.NETWORK_ERR (504)
     * @throws WarehouseErrorCode.UNKNOWN_ERR (500)
     */
    async updateProduct(warehouseId, product) {
        try {
            return await this.put(`${this.getWarehouseUrl(warehouseId)}/products`, {
                product,
            });
        }
        catch (ex) {
            return this.handleError("updateProduct", ex, warehouseId, product);
        }
    }
    /**
     * Gets the current item/product attributes, excluding quantities, from warehouse/provider
     * @param warehouseId - ID to query status from - only used to parse out provider, no need to call per warehouse
     * @param dsku - the Deliverr sku for this product
     * @returns undefined - when the product is not found
     * @returns {WarehouseProduct} when it is
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseGetProductDetailErrorCode
     */
    async getProductDetail(warehouseId, dsku) {
        try {
            return await this.get(`${this.getWarehouseUrl(warehouseId)}/products/${dsku}`);
        }
        catch (ex) {
            return this.handleError("getProductDetail", ex, warehouseId, dsku);
        }
    }
    /**
     * sends asn to warehouse
     * @param warehouseId - the warehouse the ASN is for.
     * @param asnData - data to create an ASN.
     * @returns void response body, status 200 - when asn creation is successful
     * @returns error response, status 500 - when asn creation fails
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseCreateAsnErrorCode
     */
    async createAsn(warehouseId, asnData) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/asns`, asnData);
        }
        catch (err) {
            return this.handleError("createAsn", err, warehouseId, asnData);
        }
    }
    /**
     * gets the status of asns in warehouse
     * @param warehouseId
     * @param deliverrAsnId
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseGetAsnStatusErrorCode
     */
    async getAsnStatus(warehouseId, deliverrAsnId, versionNumber = 1) {
        try {
            const warehouseUrl = this.getWarehouseUrl(warehouseId, versionNumber);
            return await this.get(`${warehouseUrl}/asns/${deliverrAsnId}`, { warehouseId });
        }
        catch (err) {
            return this.handleError("getAsnStatus", err, warehouseId, deliverrAsnId);
        }
    }
    /**
     * @param warehouseId - warehouse identifier.
     * @param fromTime - start date-time of search range.
     * @param toTime  - end date-time of search range.
     * @param page - optional. The page to query
     */
    async getAsnReceipt(warehouseId, fromTime, toTime, page) {
        try {
            return await this.get(`${this.getWarehouseUrl(warehouseId)}/asn/receipt`, {
                warehouseId,
                fromTime,
                toTime,
                page,
            });
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: ReceiptAsnResponse_1.ReceiptAsnErrorCode.UNKNOWN }, () => ({ receiptLogs: [] }), () => ({ receiptLogs: [] }), () => ({ receiptLogs: [] }));
        }
    }
    /**
     * gets the status of asns in warehouse
     * Use this endpoint for the warehouses which are
     * enabled for lot tracking and expiration date.
     * @param warehouseId
     * @param deliverrAsnId
     */
    async getAsnStatusV2(warehouseId, deliverrAsnId) {
        return this.getAsnStatus(warehouseId, deliverrAsnId, 2);
    }
    /**
     * Receive a DSKU against an existing ASN.
     *
     * @param requestId - Unique request identifier for the receive.
     * @param asnId - Deliverr ASN identifier to receive against.
     * @param dsku - Deliverr SKU to receive.
     * @param receivedDelta - Number of units to receive.
     * @param warehouseId - Deliverr warehouse identifier.
     * @param location - Optional. String representing the location name or identifier to receive against.
     * @param expirationDate - Optional. Date representing the expiration date of the dsku
     * @param lotNumber - Optional. String representing the lot of the dsku
     * @param sscc - Optional. String representing the case, pallet or parcel
     * @param quarantineState - Optional. String representing the state of the item
     * @return Object containing current status of the receive.
     */
    async receiveAsn(requestId, asnId, dsku, receivedDelta, warehouseId, location, expirationDate, lotNumber, sscc, quarantineState, preferAsync) {
        const ctx = {
            fn: "receiveAsn",
            requestId,
            asnId,
            dsku,
            receivedDelta,
            warehouseId,
        };
        commons_logs_1.log.info(ctx);
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/asns/receive`, {
                requestId,
                asnId,
                sku: dsku,
                receivedDelta,
                warehouseId,
                location,
                expirationDate,
                lotNumber,
                sscc,
                quarantineState,
                preferAsync,
            });
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: ReceiveAsnResponse_1.ReceiveAsnErrorCode.UNKNOWN }, () => ({ status: ReceiveAsnResponse_1.ReceiveAsnStatus.FAILED }), () => ({ status: ReceiveAsnResponse_1.ReceiveAsnStatus.PENDING }), () => ({ status: ReceiveAsnResponse_1.ReceiveAsnStatus.FLAGGED }), [400, 404, 422, 500, 503], [504]);
        }
    }
    /**
     * Verify that a receive has been made with the given request identifier against an ASN.
     *
     * @param requestId - unique request identifier for the receive
     * @param asnId - expect the receive to be against this Deliverr ASN identifier
     * @param dsku - expect the receive to be against this Deliverr SKU
     * @param receiveTimestamp - receive is guaranteed to have happened on or after this timestamp
     * @param warehouseId - Deliverr warehouse identifier
     * @return object containing current status of the receive
     */
    async verifyAsnReceive(requestId, asnId, dsku, receiveTimestamp, warehouseId) {
        const ctx = {
            fn: "verifyAsnReceive",
            requestId,
            asnId,
            dsku,
            receiveTimestamp,
            warehouseId,
        };
        commons_logs_1.log.info(ctx);
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/asns/verify`, {
                requestId,
                asnId,
                sku: dsku,
                warehouseId,
                receiveTimestamp,
            });
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: ReceiveAsnResponse_1.ReceiveAsnErrorCode.UNKNOWN }, () => ({ status: ReceiveAsnResponse_1.ReceiveAsnStatus.FAILED }), () => ({ status: ReceiveAsnResponse_1.ReceiveAsnStatus.PENDING }), () => ({ status: ReceiveAsnResponse_1.ReceiveAsnStatus.FLAGGED }), [400, 404, 422, 500, 503], [504]);
        }
    }
    /**
     * Send the order to the warehouse.
     * @param shipmentId - Deliverr shipment ID to cancel
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseCancelShipmentErrorCode
     */
    async cancelShipment(warehouseId, shipmentId) {
        commons_logs_1.log.info("cancelShipment: ", shipmentId, " warehouseId: ", warehouseId);
        try {
            return await this.delete(`${this.getWarehouseUrl(warehouseId)}/shipments/${shipmentId}/warehouse/${warehouseId}`);
        }
        catch (ex) {
            return this.handleError("cancelShipment", ex, warehouseId, shipmentId);
        }
    }
    /**
     * Update the order at the warehouse.
     * @param shipmentId - Deliverr shipment ID to update
     * @param updateRequest - ShipmentRequest fields to update
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseCancelShipmentErrorCode
     */
    async updateShipment(warehouseId, shipmentId, updateRequest) {
        commons_logs_1.log.info("updateShipment: ", updateRequest, " shipmentId: ", shipmentId, " warehouseId: ", warehouseId);
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/shipments/${shipmentId}/warehouse/${warehouseId}`, updateRequest);
        }
        catch (ex) {
            return this.handleError("updateShipment", ex, warehouseId, shipmentId);
        }
    }
    /**
     * Get available sku inventory at warehouse
     * @param warehouseId - Deliverr warehouse ID
     * @param sku - Sku to get inventory
     * @throws BAD_WAREHOUSE_ERR
     * @throws SKU_NOT_FOUND
     */
    async getInventory(warehouseId, sku) {
        try {
            return await this.get(`${this.getWarehouseUrl(warehouseId)}/inventories`, {
                sku,
                warehouse: warehouseId,
            });
        }
        catch (ex) {
            return this.handleError("getInventory", ex, warehouseId, sku);
        }
    }
    /**
     * Create inventory snapshot at warehouse
     * @param warehouseId - Deliverr warehouse ID
     * @throws BAD_WAREHOUSE_ERR
     */
    async createInventorySnapshot(warehouseId) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/inventories/create-inventory-snapshot`, warehouseId);
        }
        catch (ex) {
            return this.handleError("createInventorySnapshot", ex, warehouseId);
        }
    }
    /**
     * Get snapshot of inventory at warehouse
     * @param snapshotId - Warehouse snapshot ID
     * @param warehouseId - Deliverr warehouse ID
     * @param sku - Sku to get inventory
     * @param pagination - Pagination preference
     * @throws BAD_WAREHOUSE_ERR
     * @throws SKU_NOT_FOUND
     */
    async getInventorySnapshot(snapshotId, warehouseId, pagination, sku) {
        try {
            return await this.get(`${this.getWarehouseUrl(warehouseId)}/inventories/get-inventory-snapshot`, {
                snapshotId,
                warehouseId,
                sku,
                page: pagination?.page,
                pageSize: pagination?.pageSize,
            });
        }
        catch (ex) {
            return this.handleError("getInventorySnapshot", ex, warehouseId, sku);
        }
    }
    /**
     * Get available inventory for all skus at warehouse
     * @param warehouseId - Deliverr warehouse ID
     * @throws BAD_WAREHOUSE_ERR
     * @throws SKU_NOT_FOUND
     */
    async getInventories(warehouseId, page) {
        try {
            return await this.get(`${this.getWarehouseUrl(warehouseId)}/warehouse-inventories`, {
                warehouse: warehouseId,
                page,
            });
        }
        catch (ex) {
            commons_logs_1.log.info(ex);
            return this.handleError("getInventories", ex, warehouseId);
        }
    }
    /**
     * Get inventory movements at warehouse
     * @param warehouseId - Deliverr warehouse ID
     * @param fromTime - start date of search range
     * @param toTime - end date of search range
     * @param reduceByWmsInventoryId - add wmsInventoryId as unique identifier
     * @param includeUnsupportedMovementTypes - Include movements with unsupportedMovementType = true
     * @throws BAD_WAREHOUSE_ERR
     */
    async getInventoryMovements(warehouseId, fromTime, toTime, page, reduceByWmsInventoryId, includeUnsupportedMovementTypes) {
        try {
            return await this.get(`${this.getWarehouseUrl(warehouseId)}/inventories/movements`, {
                warehouse: warehouseId,
                fromTime,
                toTime,
                page,
                reduceByWmsInventoryId,
                includeUnsupportedMovementTypes,
            });
        }
        catch (ex) {
            return this.handleError("getInventoryMovements", ex, warehouseId, {
                fromTime,
                toTime,
                page,
            });
        }
    }
    async getWarehouseLinks(getWarehouseLinkRequest) {
        commons_logs_1.log.info(`getWarehouseLinks:`, getWarehouseLinkRequest);
        try {
            return await this.getCached(`/DATA/v1/warehouse-links`, getWarehouseLinkRequest, 10 * MILLISECONDS_PER_MINUTE);
        }
        catch (ex) {
            return this.handleError("getWarehouseLinks", ex, getWarehouseLinkRequest.fromWarehouseId, getWarehouseLinkRequest.toWarehouseId);
        }
    }
    async setAllOutgoingWarehouseLinks(fromWarehouseId, warehouseLinks) {
        commons_logs_1.log.info(`setAllOutgoingWarehouseLinks:`, {
            fromWarehouseId,
            warehouseLinks,
        });
        try {
            return await this.post(`/DATA/v1/warehouses/${fromWarehouseId}/warehouse-links`, { links: warehouseLinks });
        }
        catch (ex) {
            return this.handleError("setAllOutgoingWarehouseLinks", ex, fromWarehouseId, warehouseLinks);
        }
    }
    async saveWarehouseLink(warehouseLink) {
        commons_logs_1.log.info(`saveWarehouseLink:`, { warehouseLink });
        try {
            return await this.post(`/DATA/v1/warehouses/warehouse-links`, warehouseLink);
        }
        catch (ex) {
            return this.handleError("saveWarehouseLink", ex, warehouseLink.fromWarehouseId, warehouseLink);
        }
    }
    // The purpose of this method is to deactivate a WarehouseLink,
    // by passing warehouseLinkUpdate = { isActive: false }
    async updateWarehouseLink(warehouseLinkId, warehouseLinkUpdate) {
        commons_logs_1.log.info(`updateWarehouseLink:`, { warehouseLinkId, warehouseLinkUpdate });
        try {
            return await this.post(`/DATA/v1/warehouses/warehouse-links/${warehouseLinkId}`, warehouseLinkUpdate);
        }
        catch (ex) {
            return this.handleError("updateWarehouseLink", ex, warehouseLinkId.toString(), warehouseLinkUpdate);
        }
    }
    async getWarehouseToHub() {
        const warehouseLinks = await (0, commons_utils_1.retryNTimesWithPause)(4, 2000, undefined, async () => this.getWarehouseLinks({}));
        const result = {};
        for (const { toWarehouseId, hubId } of warehouseLinks || []) {
            if (!result[toWarehouseId]) {
                result[toWarehouseId] = hubId || null;
            }
        }
        return result;
    }
    async getLocationIds(warehouseId, inputParams) {
        try {
            let locationIds = [];
            let maxPage;
            let page = 1;
            do {
                const response = await (0, commons_utils_1.retryNTimesWithPause)(3, 500, undefined, async () => {
                    return this.getCached(`${this.getWarehouseUrl(warehouseId)}/locations`, {
                        warehouseId,
                        page,
                        isLocked: inputParams?.isLocked,
                        isPickable: inputParams?.isPickable,
                    });
                });
                if (response.data.locations.length > 0) {
                    maxPage = response.data.locations[0].pageCount || 0;
                    locationIds = locationIds.concat(response.data.locations.map((detail) => detail.code));
                }
                else {
                    maxPage = 1;
                }
                page += 1;
            } while (page <= maxPage);
            return {
                data: { locationIds },
            };
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: ReceiveLocationResponse_1.ReceiveLocationErrorCode.UNKNOWN }, () => ({ locationIds: [] }), () => ({ locationIds: [] }), () => ({ locationIds: [] }), [400, 404, 422, 500, 503], [504]);
        }
    }
    async getLocations(warehouseId, inputParams) {
        try {
            let locationData = [];
            let maxPage;
            let page = inputParams?.pageNumber ?? 1;
            do {
                const response = await (0, commons_utils_1.retryNTimesWithPause)(3, 500, undefined, async () => {
                    return this.getCached(`${this.getWarehouseUrl(warehouseId)}/locations`, {
                        warehouseId,
                        page,
                        locationCode: inputParams?.locationCode,
                    });
                });
                if (response.data.locations.length > 0) {
                    maxPage = response.data.locations[0].pageCount || 0;
                    locationData = locationData.concat({
                        page,
                        pageCount: maxPage,
                        locations: response.data.locations,
                    });
                }
                else {
                    maxPage = 1;
                }
                page += 1;
            } while (page <= maxPage && !inputParams?.pageNumber);
            return {
                data: locationData,
            };
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: ReceiveLocationResponse_1.ReceiveLocationErrorCode.UNKNOWN }, () => [], () => [], () => [], [400, 404, 422, 500, 503], [504]);
        }
    }
    /**
     * @deprecated Use getInventoryByLocation
     */
    async getInventoryByLocationV2(requestData) {
        return await this.getInventoryByLocation(requestData);
    }
    async getInventoryByLocation(requestData) {
        try {
            let InventoryResponse = [];
            let maxPage;
            let pageNumber = 1;
            const isSpecificPage = requestData.page ? true : false;
            do {
                const response = await this.get(`${this.getWarehouseUrl(requestData.warehouseId)}/location-inventories`, { ...requestData, page: requestData.page ?? 1 });
                if (response.inventories.length > 0) {
                    maxPage = response.pageCount;
                    InventoryResponse = InventoryResponse.concat(response);
                    if (isSpecificPage) {
                        break;
                    }
                }
                else {
                    maxPage = 1;
                }
                pageNumber += 1;
                requestData.page = pageNumber;
            } while (pageNumber <= maxPage);
            return {
                data: InventoryResponse,
            };
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: InventoryByLocationResponse_1.InventoryByLocationErrorCode.UNKNOWN }, () => [], () => [], () => [], [400, 404, 422, 500, 503], [504]);
        }
    }
    async getServiceLambdaVersion(serviceUrlPath) {
        commons_logs_1.log.info(`getServiceLambdaVersion:`, { serviceUrlPath });
        try {
            return await this.get(`/DATA/v1/service-versions/${serviceUrlPath}`);
        }
        catch (ex) {
            return this.handleError("getServiceLambdaVersion", ex);
        }
    }
    async updateServiceLambdaVersion(serviceLambdaVersion) {
        commons_logs_1.log.info(`updateServiceLambdaVersion:`, { serviceLambdaVersion });
        try {
            return await this.post(`/DATA/v1/service-versions`, { serviceLambdaVersion });
        }
        catch (ex) {
            return this.handleError("updateServiceLambdaVersion", ex);
        }
    }
    /**
     * Grabs statuses for given set of deliverr's shipment IDs
     * @param warehouseId - warehouse ID that the shipment lives in
     * @param shipmentIds - the set of IDs we want a status for
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseGetShipmentsErrorCode
     */
    async getShipments(warehouseId, shipmentIds, includeSSCC = false) {
        return this.post(`${this.getWarehouseUrl(warehouseId)}/shipments/fetch-status`, { deliverrShipmentIds: shipmentIds, warehouseId, includeSSCC });
    }
    /**
     * Grabs statuses for given set of deliverr's shipment IDs - asyncly
     * @param warehouseId - warehouse ID that the shipment lives in
     * @param shipmentIds - the set of IDs we want a status for
     * @throws WarehouseCommonErrorCode
     * @throws WarehouseGetShipmentsErrorCode
     */
    async getShipmentsAsync(warehouseId, shipmentIds, includeSSCC = false) {
        return this.post(`${this.getWarehouseUrl(warehouseId)}/shipments/fetch-status-async`, { deliverrShipmentIds: shipmentIds, warehouseId, includeSSCC, isAsync: true });
    }
    /**
     * submits sortCodes mapping of shipping methods to warehouse
     * @param warehouseId - warehouse ID to send the sortCodes to
     * @param sortCodeMappings - the SortCodeMappings object
     */
    async submitSortCodes(warehouseId, sortCodeMappings) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/sort-codes`, sortCodeMappings);
        }
        catch (ex) {
            return this.handleError("submitSortCodes", ex, warehouseId, sortCodeMappings);
        }
    }
    async searchWarehouses(address, capabilities = []) {
        commons_logs_1.log.info({ capabilities, address }, "Searching warehouses by criteria");
        const capabilitiesParam = capabilities.reduce((mapped, capability) => ({
            ...mapped,
            [capability]: true,
        }), {});
        try {
            return await this.post(`/DATA/v1/warehouses/search`, {
                ...capabilitiesParam,
                address,
            });
        }
        catch (ex) {
            this.handleError("updateWarehouseLink", ex, "", {
                address,
                capabilities,
            });
            return [];
        }
    }
    /**
     * Returns WarehouseEventDetails for a given warehouse id
     */
    async getWarehouseEventDetails(warehouseId) {
        commons_logs_1.log.info(`getWarehouseEventDetails`);
        return this.post(`/DATA/v1/warehouse-event-details`, {
            warehouseId,
        });
    }
    /**
     * Quarantines a lot with logiwa
     * @param quarantineRequest - contains the following fields:
     * requestId - a unique id for each quarantine request
     * dsku - the dsku of the product to be quarantined
     * warehouseId - the warehouse in which we want to quarantine
     * isQuarantine - [optional, default false] set to false is un-quarantining
     * quarantineReason - [optional] reason to quarantine the lot
     * lotNumber - [optional] the lot number to be quarantined
     * expirationDate - [optional] the expiration date of the lot to be quarantined
     */
    async quarantineLot(quarantineRequest) {
        return this.post(`${this.getWarehouseUrl(quarantineRequest.warehouseId)}/quarantine-lot`, undefined, quarantineRequest);
    }
    /**
     * To carry out serial audit at warehouse
     * @param warehouseId - warehouse ID to audit
     * @param serialRequest - the SerialInventories object
     */
    async getInventoriesSerial(warehouseId, serialRequest) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/inventories-serial`, serialRequest);
        }
        catch (ex) {
            return this.handleError("getInventoriesSerial", ex, warehouseId, serialRequest);
        }
    }
    /**
     * To carry out bulk audit at warehouse
     * @param warehouseId - warehouse ID to audit
     * @param bulkRequest - the BulkInventories object
     */
    async getInventoriesBulk(warehouseId, bulkRequest) {
        try {
            return await this.post(`${this.getWarehouseUrl(warehouseId)}/inventories-bulk`, bulkRequest);
        }
        catch (ex) {
            return this.handleError("getInventoriesBulk", ex, warehouseId, bulkRequest);
        }
    }
    getWarehouseUrl(warehouseId, version = 1) {
        return `/${warehouseId}/v${version}`;
    }
    /**
     * Returns a jitter between [-delay, delay].
     */
    getJitterValue(delay) {
        return Math.floor(Math.random() * 2 * delay - delay);
    }
}
exports.WarehouseClient = WarehouseClient;
//# sourceMappingURL=WarehouseClient.js.map